import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Ban, Loader2 } from "lucide-react";

export default function BlockedUsersPage() {
  const [rows, setRows] = useState(null);
  const [busyId, setBusyId] = useState("");

  useEffect(() => {
    api.get("/users/me/blocks")
      .then(({ data }) => setRows(Array.isArray(data) ? data : []))
      .catch(() => setRows([]));
  }, []);

  const unblock = async (u) => {
    setBusyId(u.id);
    try {
      await api.delete(`/users/${u.id}/block`);
      setRows((cur) => cur.filter((r) => r.id !== u.id));
      toast.success(`Unblocked ${u.display_name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not unblock.");
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-2xl" data-testid="blocked-users-page">
      <div className="text-xs uppercase tracking-[0.32em] text-rose-500 mb-1">Safety</div>
      <h1 className="font-display text-4xl tracking-tight mb-2">Blocked users</h1>
      <p className="text-sm text-zinc-500 mb-6">
        Blocked users can't message, nudge, or request your private photos — and you're
        mutually invisible on Social Meet.
      </p>
      {rows === null && (
        <div className="flex items-center gap-2 text-zinc-500 text-sm"><Loader2 size={14} className="animate-spin" /> Loading…</div>
      )}
      {rows?.length === 0 && (
        <div className="card-surface p-8 text-center text-sm text-zinc-500" data-testid="blocked-empty">
          <Ban size={22} className="mx-auto mb-2 text-zinc-600" />
          You haven't blocked anyone.
        </div>
      )}
      <div className="space-y-2">
        {rows?.map((u) => (
          <div key={u.id} data-testid={`blocked-row-${u.id}`} className="card-surface px-4 py-3 flex items-center gap-3">
            <Avatar className="w-10 h-10 border border-white/10">
              <AvatarImage src={u.avatar_url} />
              <AvatarFallback className="bg-rose-600 text-white text-sm">{u.display_name?.[0]?.toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <Link to={`/u/${u.id}`} className="text-sm text-white hover:text-rose-400 truncate block">
                {u.display_name}
              </Link>
              <div className="text-xs text-zinc-500">{u.display_name}</div>
            </div>
            <Button
              onClick={() => unblock(u)}
              disabled={busyId === u.id}
              data-testid={`blocked-unblock-${u.id}`}
              className="rounded-full bg-white/10 border border-white/15 text-white hover:border-rose-500 px-4"
            >
              {busyId === u.id ? <Loader2 size={13} className="animate-spin" /> : "Unblock"}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
